import DOMPurify from 'dompurify';
import React from 'react';

const ALLOWED_TAGS = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'a',
    'ul',
    'ol',
    'li',
    'code',
    'pre',
    'span',
    'div',
    'img'
];
const ALLOWED_ATTR = [
    'href',
    'target',
    'rel',
    'src',
    'alt',
    'width',
    'height',
    'data-type',
    'loading'
];

export function sanitizeHtml(html: string): string {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

export function isHtmlEmpty(html: string): boolean {
    return html.replace(/<[^>]*>/g, '').trim().length === 0;
}

function highlightTextNodes(
    container: HTMLElement,
    query: string,
    activeGlobalIdx: number,
    startIdx: number
): void {
    if (!query) return;
    const needle = query.toLowerCase();
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
        textNodes.push(node as Text);
    }

    let globalIdx = startIdx;
    for (const textNode of textNodes) {
        const text = textNode.textContent ?? '';
        const hay = text.toLowerCase();
        const parts: (string | { match: string; idx: number })[] = [];
        let last = 0;
        let pos = hay.indexOf(needle, 0);
        while (pos !== -1) {
            if (pos > last) parts.push(text.slice(last, pos));
            parts.push({ match: text.slice(pos, pos + needle.length), idx: globalIdx });
            globalIdx++;
            last = pos + needle.length;
            pos = hay.indexOf(needle, last);
        }
        if (parts.length === 0) continue;
        if (last < text.length) parts.push(text.slice(last));

        const parent = textNode.parentNode;
        if (!parent) continue;
        const frag = document.createDocumentFragment();
        for (const part of parts) {
            if (typeof part === 'string') {
                frag.appendChild(document.createTextNode(part));
            } else {
                const mark = document.createElement('mark');
                mark.textContent = part.match;
                mark.dataset.matchIdx = String(part.idx);
                mark.className =
                    part.idx === activeGlobalIdx
                        ? 'bg-yellow-300 ring-1 ring-yellow-400 rounded px-0.5'
                        : 'bg-yellow-200 rounded px-0.5';
                frag.appendChild(mark);
            }
        }
        parent.replaceChild(frag, textNode);
    }
}

interface HtmlContentProps {
    html: string;
    className?: string;
    onImageClick?: (src: string) => void;
    highlightQuery?: string;
    highlightActiveIdx?: number;
    highlightStartIdx?: number;
}

const HtmlContent: React.FC<HtmlContentProps> = ({
    html,
    className,
    onImageClick,
    highlightQuery,
    highlightActiveIdx = -1,
    highlightStartIdx = 0
}) => {
    const clean = sanitizeHtml(html);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!ref.current) return;

        // Reset DOM to clean HTML before any mutations (highlighting may have fragmented text nodes)
        ref.current.innerHTML = clean;

        // Image click handlers
        if (onImageClick) {
            const imgs = ref.current.querySelectorAll('img');
            const handler = (e: Event) => {
                const src = (e.currentTarget as HTMLImageElement).src;
                if (src) onImageClick(src);
            };
            imgs.forEach((img) => {
                img.style.cursor = 'pointer';
                img.addEventListener('click', handler);
            });
        }

        // Search highlighting
        if (highlightQuery?.trim()) {
            highlightTextNodes(
                ref.current,
                highlightQuery.trim(),
                highlightActiveIdx,
                highlightStartIdx
            );
        }
    }, [clean, onImageClick, highlightQuery, highlightActiveIdx, highlightStartIdx]);

    return (
        <div
            ref={ref}
            className={`prose-chat ${className ?? ''}`}
            dangerouslySetInnerHTML={{ __html: clean }}
        />
    );
};

export { HtmlContent };
